import { Building2, Crown, Hammer, LucideIcon } from 'lucide-react';

// ============================================
// STYLING CONSTANTS
// ============================================

export const STYLES = {
  // Section backgrounds
  section: {
    dark: 'bg-maintenance-dark',
    navy: 'bg-maintenance-navy',
    light: 'bg-white',
  },

  // Card styling
  card: 'bg-maintenance-navy/40 border border-white/15 text-white backdrop-blur-md shadow-xl rounded-xl',
  cardSolid: 'bg-maintenance-navy border border-white/10 text-white shadow-xl rounded-xl',

  // Button styling
  button: {
    primary: 'bg-maintenance-navy text-white hover:bg-maintenance-blue border border-white/30 hover:border-white transition-colors',
    ghost: 'bg-white/10 text-white hover:bg-white/20 border border-white/30 hover:border-white/60 transition-colors',
    link: 'text-white hover:text-maintenance-blue transition-colors',
  },

  // Form input styling
  input: 'bg-maintenance-dark/60 border-white/20 text-white placeholder:text-white/30 focus:border-white/60 focus-visible:ring-0',

  // Text styles
  text: {
    heading: 'text-white font-bold',
    body: 'text-white/70',
    muted: 'text-white/50',
    accent: 'text-maintenance-blue',
    label: 'text-maintenance-blue text-xs uppercase tracking-widest font-semibold',
  },

  // Background overlays
  overlay: {
    subtle: 'bg-black/20',
    medium: 'bg-black/50',
    heroGradient: 'bg-gradient-to-b from-maintenance-navy/80 via-maintenance-slate/75 to-maintenance-navy/70',
    cardGradient: 'bg-gradient-to-t from-maintenance-navy via-maintenance-navy/60 to-transparent',
  },

  // Dividers
  divider: 'border-white/10',

  // Navigation
  nav: {
    background: 'bg-maintenance-navy/80 backdrop-blur-sm',
    menuItem: 'text-white hover:bg-maintenance-blue/20 cursor-pointer transition-colors',
    link: 'text-white hover:text-white/70 transition-colors font-medium',
  },

  // Icon containers
  icon: {
    primary: 'bg-maintenance-blue/20 rounded-lg p-3',
    subtle: 'bg-white/10 rounded-lg p-2',
  },
} as const;

// ============================================
// SECTION DATA
// ============================================

export interface SectionData {
  id: string;
  backgroundImage: string;
  title: string;
  subtitle?: string;
  centered?: boolean;
}

export const SECTIONS: Record<string, SectionData> = {
  summary: {
    id: 'summary',
    backgroundImage: '/static/images/2023-09-21_20-32-25.440-p-1080.webp',
    title: 'A Jobsite Documentation Platform, Built for Construction',
    subtitle: "Ironview 360 solves today's site documentation issues with an easy-to-use platform that allows you to follow a project's progress from anywhere, anytime. We create comprehensive, permanent records of your construction projects, allowing you to focus on completing jobs on schedule and budget.",
    centered: true,
  },
  businessValue: {
    id: 'words',
    backgroundImage: '/static/images/pt_deck_1600.webp',
    title: 'Business value, delivered.',
    subtitle: 'We provide comprehensive construction documentation services to deliver the most accurate and fastest project information available. Underground documentation, drone service for each deck, 360-degree progress documentation. Your project memorialized for clashes, liability concerns, as-built accuracy, and maintenance. For one low price, we provide these services turnkey, with unmatched density and speed.',
    centered: true,
  },
  aboutUs: {
    id: 'about-us',
    backgroundImage: '/static/images/2023-09-22_08-41-13.164-p-1600.webp',
    title: 'One low price providing unparalleled density and speed',
    subtitle: "Since 2017, Ironview 360 has documented over 5 million square feet of building projects with photos and video. We have grown into an agile construction services company that blends the latest capture technology with unmatched customer care to suit construction professionals' visual construction documentation demands.",
    centered: true,
  },
  learnMore: {
    id: 'learn-more',
    backgroundImage: '/static/images/parq-1600.webp',
    title: 'Getting started is easy',
    subtitle: "Schedule a lunch & learn below to see for yourself how Ironview 360 can save you $$$ in litigation costs, hundreds of hours in wasted communication and travel time, and eliminate the risk of slipping project deadlines that could cost you millions.",
    centered: true,
  },
};

// ============================================
// INDUSTRY SECTIONS DATA
// ============================================

export interface IndustryData {
  id: string;
  title: string;
  icon: LucideIcon;
  backgroundImage: string;
  content: string[];
  backToTopColor?: 'text-white' | 'text-black';
}

export const INDUSTRIES: IndustryData[] = [
  {
    id: 'general-contractors',
    title: 'General Contractors',
    icon: Building2,
    backgroundImage: '/static/images/GC-p-1600.webp',
    backToTopColor: 'text-white',
    content: [
      'Ensure accountability, streamline collaboration & coordination, eliminate disagreements, and decrease travel - all while delivering on time and budget.',
      'Quicker coordination & higher accountability. All done virtually. Effective progress tracking and issue resolution. Documented quality control for all parties.',
    ],
  },
  {
    id: 'specialty-contractors',
    title: 'Specialty Contractors',
    icon: Hammer,
    backgroundImage: '/static/images/successful-subcontractor-2020-scaled.webp',
    backToTopColor: 'text-white',
    content: [
      'Keep track of in-place work, communicate with other trades, and record your finished work so you can get paid on time.',
      'Record progress and get paid on time.',
      'Validate work virtually.',
      'Manage your workforce more effectively.',
    ],
  },
  {
    id: 'owners',
    title: 'Owners',
    icon: Crown,
    backgroundImage: '/static/images/Owner-p-1600.webp',
    backToTopColor: 'text-white',
    content: [
      'Resolve conflicts quicker by managing all projects remotely. Save on long-term maintenance costs.',
      'Manage Risk',
      'Simplify Operation & Maintenance',
      'Deliver on time and Under Budget',
    ],
  },
];

// ============================================
// NAVIGATION DATA
// ============================================

export const NAV_LINKS = [
  { id: 'about-us', label: 'About Us' },
  { id: 'demos', label: 'Demos' },
] as const;

export const SERVICE_LINKS = [
  { id: 'maintenance', label: 'Maintenance', href: 'https://ironviewmaintenance.com' },
] as const;

export const SOLUTION_LINKS = [
  { id: 'general-contractors', label: 'General Contractors' },
  { id: 'specialty-contractors', label: 'Specialty Contractors' },
  { id: 'owners', label: 'Owners' },
] as const;

export const FOOTER_NAV_LINKS = [
  { id: 'about-us', label: 'About Us' },
  { id: 'demos', label: 'Demos' },
  { id: 'general-contractors', label: 'General Contractors' },
  { id: 'specialty-contractors', label: 'Specialty Contractors' },
  { id: 'owners', label: 'Owners' },
] as const;

export const FOOTER_LEGAL_LINKS = [
  { id: 'privacy', label: 'Privacy Policy' },
  { id: 'terms', label: 'Terms of Service' },
  { id: 'cookies', label: 'Cookies Settings' },
] as const;

// ============================================
// CONTACT INFO
// ============================================

export const CONTACT_INFO = {
  email: 'timur@ironview360.com',
  phone: '720.635.8603',
  copyright: '© 2026 Ironview 360. All rights reserved.',
} as const;

// ============================================
// DEMO SECTION DATA
// ============================================

export const DEMO_SECTION = {
  id: 'demos',
  backgroundImage: '/static/images/gvr_aerial.webp',
  title: 'The largest construction companies trust Ironview 360 services',
  subtitle: 'See for yourself with our interactive demos below',
  clientsImage: '/static/images/clients-list.webp',
} as const;
